import { applyResponderUpdate } from './memberStates.js';

const toLatLng = p => ({ lat: Number(p?.latitude ?? p?.lat), lng: Number(p?.longitude ?? p?.lng) });
const metersBetween = (a, b) => {
    const r = 6371000, rad = Math.PI / 180;
    const dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
    return 2 * r * Math.asin(Math.sqrt(h));
};

/**
 * Ce tracé est le parcours visé (mêmes extrémités, à 100 m près ; départ éventuellement rogné sur
 * le parcours) : le parcours a démarré. Un itinéraire d'approche rogné n'en est pas un.
 */
/** Plus d'itinéraire ni de parcours sur la carte (quitté, alerte, fin de session). */
function clearRoute(session) {
    session.routePoints = [];
    session.corridorZone = [];
    session.corridorHoles = [];
    session.destinationLocation = null;
    session.trailApproachPoints = [];
    session.trailApproachLeaving = false;
}

export function isSameTrail(route, trail) {
    if (!Array.isArray(route) || !Array.isArray(trail) || route.length < 2 || trail.length < 2) return false;
    const r = route.map(toLatLng), t = trail.map(toLatLng);
    if (metersBetween(r[r.length - 1], t[t.length - 1]) > 100) return false;
    return t.some(p => metersBetween(r[0], p) <= 100);
}

/**
 * Déballe les données d'un événement (gestion de la structure Android/JSON)
 */
export function unwrapEventData(val) {
    if (!val) return null;
    let data = val;
    if (typeof val === 'string') {
        try { data = JSON.parse(val); } catch (e) { return val; }
    }
    if (data && typeof data === 'object' && data.value !== undefined) {
        return unwrapEventData(data.value);
    }
    return data;
}

const asBoolean = value => value === true || value === 'true';
const asNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};
const coordinate = (object, primary, fallback) => object?.[primary] ?? object?.[fallback];

/**
 * Retourne l'horodatage canonique auquel l'événement modifie la session.
 * L'enveloppe MQTT est la référence : les timestamps GPS internes restent des
 * métadonnées de capture et ne doivent pas faire changer la couleur avant que
 * l'événement de conversion ait réellement été appliqué.
 */
export function getEventTimestamp(event, fallback = 0) {
    const timestamp = Number(event?.timestamp);
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : fallback;
}

function normalizePosition(rawPosition, eventTimestamp, sessionType) {
    const position = unwrapEventData(rawPosition);
    if (!position || typeof position !== 'object') return null;

    const lat = Number(coordinate(position, 'latitude', 'lat'));
    const lng = Number(coordinate(position, 'longitude', 'lng'));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    const sourceTimestamp = asNumber(position.timestamp, eventTimestamp);
    return {
        ...position,
        lat,
        lng,
        latitude: lat,
        longitude: lng,
        // timestamp pilote le replay. sourceTimestamp conserve l'heure GPS.
        timestamp: eventTimestamp || sourceTimestamp,
        sourceTimestamp,
        sessionType: position.sessionType || sessionType || null,
        isKeyEvent: asBoolean(position.isKeyEvent),
        stopTimerValue: asNumber(position.stopTimerValue, 0),
    };
}

/**
 * Met à jour l'objet virtualSession en fonction du type d'événement
 * @returns {Object|null} Objet décrivant un changement majeur nécessitant une réaction UI, ou null.
 */
export function processEvent(event, session, silent = false) {
    let triggeredChange = null;
    const type = event.eventType;
    const eventTimestamp = getEventTimestamp(event, asNumber(session?.timestamp, 0));
    let data = event.eventData || event.value || event;

    if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (e) { }
    }

    const prop = data.property || data.propertyName;
    const val = data.value;

    if (!silent) console.log(`   🛠️ [PROCESS] Type: ${type} | Prop: ${prop || 'N/A'}`);

    try {
        switch (type) {
            case 'NEWPOSITION':
                const pos = normalizePosition(data.value || data, eventTimestamp, session.sessionType);
                if (pos) {
                    const { lat, lng, timestamp, isKeyEvent } = pos;

                    session.currentLocation = pos;
                    if (pos.stopTimerValue !== undefined && pos.stopTimerValue !== null) {
                        session.stopTimerValue = asNumber(pos.stopTimerValue, 0);
                    }
                    if (pos.sessionType) {
                        session.sessionType = pos.sessionType;
                    }

                    if (!session.victimRoute) session.victimRoute = [];
                    session.victimRoute.push({ lat, lng, timestamp, isKeyEvent, sessionType: pos.sessionType });

                    if (isKeyEvent) {
                        if (!session.keyEvents) session.keyEvents = [];
                        session.keyEvents.push({ lat, lng, timestamp, sessionType: pos.sessionType });
                    }

                    triggeredChange = { type: 'POSITION', position: pos };
                }
                break;

            case 'STATECHANGE':
                if (!prop) break;
                let parsedVal = val;
                if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
                    try { parsedVal = JSON.parse(val); } catch (e) { }
                }

                if (prop === 'isCorridorEnabled') {
                    const corridorValue = parsedVal && typeof parsedVal === 'object' ? parsedVal : null;
                    session.isCorridorEnabled = corridorValue ? asBoolean(corridorValue.enabled) : asBoolean(parsedVal);
                    if (corridorValue?.points && Array.isArray(corridorValue.points)) {
                        // Boucle : trous du corridor. Un événement sans trous (corridor activé, largeur,
                        // anciennes versions de l'app) garde ceux du même corridor.
                        const sameZone = JSON.stringify(parsedVal.points) === JSON.stringify(session.corridorZone);
                        session.corridorZone = parsedVal.points;
                        if (Array.isArray(corridorValue.holes)) session.corridorHoles = corridorValue.holes;
                        else if (!sameZone) session.corridorHoles = [];
                    }
                    if (corridorValue?.sizeMeters !== undefined) {
                        session.corridorSizeMeters = Number(parsedVal.sizeMeters) || session.corridorSizeMeters;
                    }
                } else if (prop === 'routePoints') {
                    // Itinéraire changé en cours de session (nouvelle destination, ou plus d'itinéraire : []).
                    if (Array.isArray(parsedVal)) session.routePoints = parsedVal;
                    // Tracé retiré (parcours ou itinéraire quitté) : il quitte la carte avec son corridor.
                    if (Array.isArray(parsedVal) && parsedVal.length === 0) clearRoute(session);
                    if (session.trailApproachLeaving && isSameTrail(session.routePoints, session.trailApproachPoints)) {
                        session.trailApproachPoints = [];
                        session.trailApproachLeaving = false;
                    }
                } else if (prop === 'destinationLocation') {
                    session.destinationLocation = parsedVal && typeof parsedVal === 'object' ? parsedVal : null;
                } else if (prop === 'corridorZone') {
                    if (Array.isArray(parsedVal)) {
                        session.corridorZone = parsedVal;
                        session.corridorHoles = [];
                    }
                } else if (prop === 'corridorSizeMeters') {
                    session.corridorSizeMeters = Number(parsedVal) || 25;
                } else if (prop === 'sessionType') {
                    session.sessionType = parsedVal;
                } else if (prop === 'isStopped') {
                    session.isStopped = asBoolean(parsedVal);
                } else if (prop === 'stopTimerValue') {
                    session.stopTimerValue = asNumber(parsedVal, 0);
                } else if (prop === 'isStopDetectionEnabled') {
                    session.isStopDetectionEnabled = asBoolean(parsedVal);
                } else if (prop === 'stopDetectionDurationMs') {
                    session.stopDetectionDurationMs = Number(parsedVal) || 120000;
                } else if (prop === 'isAccidentDetectionEnabled') {
                    session.isAccidentDetectionEnabled = asBoolean(parsedVal);
                } else if (prop === 'isShakeDetectionEnabled') {
                    session.isShakeDetectionEnabled = asBoolean(parsedVal);
                } else if (prop === 'isShortcutDetectionEnabled') {
                    session.isShortcutDetectionEnabled = asBoolean(parsedVal);
                } else if (prop === 'shortcutDetectionMode') {
                    session.shortcutDetectionMode = String(parsedVal);
                } else if (prop === 'audioEnabled' || prop === 'audio') {
                    session.audio = asBoolean(parsedVal);
                } else if (prop === 'videoEnabled' || prop === 'video') {
                    session.video = asBoolean(parsedVal);
                } else if (prop === 'selectedTravelMode' || prop === 'travelMode') {
                    session.travelMode = parsedVal;
                    // Suivi de position (parcours ou itinéraire quitté, alerte) : plus de tracé.
                    if (['FOLLOW', 'MEETING'].includes(String(parsedVal).toUpperCase())) clearRoute(session);
                } else if (prop === 'trailApproach') {
                    // Itinéraire de parcours : l'utilisateur se rend à ce parcours.
                    // Son tracé s'affiche avec l'itinéraire qui y mène, comme dans l'application.
                    const approach = parsedVal && typeof parsedVal === 'object' ? parsedVal : null;
                    session.trailApproachName = approach?.name || null;
                    // Approche terminée (« Rejoindre le parcours ») : le parcours reste affiché
                    // jusqu'à ce que son tracé remplace l'itinéraire.
                    if (Array.isArray(approach?.points) && approach.points.length >= 2) {
                        session.trailApproachPoints = approach.points;
                        session.trailApproachLeaving = false;
                    } else {
                        session.trailApproachLeaving = (session.trailApproachPoints || []).length > 0;
                    }
                } else if (prop === 'activityZone') {
                    // Zone de rendez-vous (contour) et son nom ; « null » : rendez-vous retiré.
                    const zone = parsedVal && typeof parsedVal === 'object' ? parsedVal : null;
                    session.meetingZoneName = zone?.name || null;
                    session.meetingZonePoints = Array.isArray(zone?.points) ? zone.points : [];
                } else if (prop === 'trailStarted') {
                    session.trailApproachName = null;
                    session.trailApproachPoints = [];
                    session.trailApproachLeaving = false;
                    session.trailStartedName = String(parsedVal || '') || null;
                } else if (prop === 'sportPause') {
                    // Sortie Sport : pause déclarée jusqu'à cette heure (ms) ; l'immobilité est attendue.
                    session.sportPauseUntil = Number(parsedVal) || null;
                } else if (prop === 'sportResume') {
                    session.sportPauseUntil = null;
                } else if (prop === 'sportCheck') {
                    // « Tout va bien ? » posé (arrêt prolongé, hors parcours) : en attente de réponse.
                    session.sportCheck = String(parsedVal);
                } else if (prop === 'sportCheckOk') {
                    session.sportCheck = null;
                } else if (prop === 'detectionOptions') {
                    if (parsedVal && typeof parsedVal === 'object') {
                        if (parsedVal.isStopDetectionEnabled !== undefined) session.isStopDetectionEnabled = asBoolean(parsedVal.isStopDetectionEnabled);
                        if (parsedVal.stopDetectionDurationMs !== undefined) session.stopDetectionDurationMs = Number(parsedVal.stopDetectionDurationMs);
                        if (parsedVal.isAccidentDetectionEnabled !== undefined) session.isAccidentDetectionEnabled = asBoolean(parsedVal.isAccidentDetectionEnabled);
                        if (parsedVal.isShakeDetectionEnabled !== undefined) session.isShakeDetectionEnabled = asBoolean(parsedVal.isShakeDetectionEnabled);
                        if (parsedVal.isShortcutDetectionEnabled !== undefined) session.isShortcutDetectionEnabled = asBoolean(parsedVal.isShortcutDetectionEnabled);
                        if (parsedVal.shortcutDetectionMode !== undefined) session.shortcutDetectionMode = String(parsedVal.shortcutDetectionMode);
                    }
                }
                break;

            case 'RESPONDER_UPDATE':
                let respInfo = unwrapEventData(val || data);
                if (typeof respInfo === 'string') {
                    try { respInfo = JSON.parse(respInfo); } catch (e) { }
                }
                // Liste (tous, avec leur état) et carte (ceux qui ont choisi d'être vus).
                if (respInfo && respInfo.userId) applyResponderUpdate(session, respInfo);
                break;

            case 'CONVERTSESSION':
                const sessionConfig = unwrapEventData(data.value || data);
                if (sessionConfig) {
                    session.sessionType = sessionConfig.sessionType;
                    session.trigger = sessionConfig.trigger;
                    session.shortcutDetectionMode = sessionConfig.shortcutDetectionMode || session.shortcutDetectionMode;
                    session.appType = sessionConfig.appType || session.appType;
                    session.activityType = sessionConfig.activityType || session.activityType;
                    session.audio = asBoolean(sessionConfig.audio);
                    session.video = asBoolean(sessionConfig.video);
                    session.shareType = sessionConfig.shareType;

                    // Android matérialise CONVERTSESSION comme une position clé.
                    // Sans cela, le type de la timeline change avant la couleur du
                    // marqueur et la carte reste sur l'ancienne position.
                    const conversionPosition = normalizePosition(
                        sessionConfig.historyPositions,
                        eventTimestamp,
                        sessionConfig.sessionType
                    );
                    if (conversionPosition) {
                        // Une conversion change uniquement le contexte de session.
                        // Les anciens événements Android contenaient parfois un
                        // stopTimerValue nul/périmé : ne jamais l'appliquer ici.
                        conversionPosition.stopTimerValue = asNumber(session.stopTimerValue, 0);
                        conversionPosition.isKeyEvent = true;
                        session.currentLocation = conversionPosition;
                        if (!session.victimRoute) session.victimRoute = [];
                        session.victimRoute.push(conversionPosition);
                        if (!session.keyEvents) session.keyEvents = [];
                        session.keyEvents.push(conversionPosition);
                    }

                    // On retourne le changement pour que UI puisse basculer
                    triggeredChange = {
                        type: 'CONVERTSESSION',
                        sessionType: sessionConfig.sessionType,
                        trigger: sessionConfig.trigger,
                        shortcutDetectionMode: session.shortcutDetectionMode,
                        appType: session.appType,
                        activityType: session.activityType,
                        audio: session.audio,
                        video: session.video,
                        shareType: sessionConfig.shareType,
                        position: conversionPosition,
                    };
                }
                break;

            case 'END':
            case 'SESSION_END':
                session.isEnded = true;
                session.status = 'END';
                clearRoute(session);
                triggeredChange = { type: 'END' };
                break;

            default:
                break;
        }
    } catch (err) {
        console.error(`❌ Erreur processEvent:`, err);
    }

    return triggeredChange;
}
